import { computed, watch, ref } from 'vue'
import { useUserStore } from '@/stores/UserStore'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { useRoute } from 'vue-router'

// Module-level singletons — Cello is a single global widget.
const _bottom = ref('18px')
const _right  = ref('18px')
let _observer = null

const getHost = () => document.getElementById('cello-widget-app')

// Inject positioning CSS directly into the shadow root so it wins over Cello's
// own `left: 12px` inline style without needing inline-style fights or a
// high-frequency MutationObserver.
const injectShadowCss = () => {
  const host = getHost()
  if (!host?.shadowRoot) return

  let style = host.shadowRoot.getElementById('cello-pos-override')
  if (!style) {
    style = document.createElement('style')
    style.id = 'cello-pos-override'
    host.shadowRoot.insertBefore(style, host.shadowRoot.firstChild)
  }
  style.textContent = `
    .fab-enter-done {
      position: fixed !important;
      left: auto !important;
      right: ${_right.value} !important;
      bottom: ${_bottom.value} !important;
    }
  `
}

// Watch only for direct childList changes on the shadow root — fires if Cello
// completely recreates its shadow DOM, NOT on every animation/state update.
const startObserver = () => {
  const host = getHost()
  if (!host?.shadowRoot || _observer) return
  _observer = new MutationObserver(() => injectShadowCss())
  _observer.observe(host.shadowRoot, { childList: true })
}

// Called by useCdpSupport so Cello stays 24px left of the support widget.
export const updateCelloPosition = (bottom, supportRight) => {
  _bottom.value = bottom
  _right.value  = `${parseInt(supportRight) + 48 + 24}px`
  injectShadowCss()
}

export const useCello = () => {
  const userStore   = useUserStore()
  const globalStore = useGlobalStore()
  const configStore = useConfigStore()
  const route       = useRoute()

  const celloToken = computed(() => configStore.config?.celloToken)

  watch(celloToken, () => {
    if (!celloToken.value || globalStore.celloAlreadyBooted) return

    window.cello = window.cello || { cmd: [] }

    window.cello.cmd.push(async function (cello) {
      try {
        await cello.boot({
          productId: import.meta.env.VITE_CELLO_PRODUCT_ID,
          token: celloToken.value,
          language: 'en',
          productUserDetails: {
            firstName: userStore.user?.first_name,
            lastName: userStore.user?.last_name,
            fullName: `${userStore.user?.first_name} ${userStore.user?.last_name}`,
            email: userStore.user?.email,
          },
        })

        globalStore.celloAlreadyBooted = true

        // fab-enter-done is set after the enter animation — retry until it exists
        requestAnimationFrame(() => {
          injectShadowCss()
          startObserver()
          setTimeout(() => injectShadowCss(), 300)
          setTimeout(() => injectShadowCss(), 800)
        })

        route.name !== 'Homepage' && window.Cello && window.Cello('hide')
      } catch (error) {
        console.error('Failed to boot cello:', error)
      }
    })
  })

  watch(() => globalStore.sidebar, (sidebarOpen) => {
    if (!window.Cello) return
    const isMobile = window.innerWidth < 768
    if (isMobile && sidebarOpen) {
      window.Cello('hide')
    } else {
      route.name !== 'Homepage' && window.Cello('show')
    }
  })
}
